import { z } from 'zod';

import { DEFAULT_RULE_ORDER, EFilterScope, ERuleType } from '../constants/rule.enums';
import type { IPipelineRetryPolicy, TPipelineRule } from '../interfaces/rule.interface';
import { parseFilterAst } from '../../filter/services/filter-dsl.compiler';

const ruleBase = { enabled: z.boolean().default(true) };

const deduplicateSchema = z.object({
  ...ruleBase,
  type: z.literal(ERuleType.DEDUPLICATE),
  keyPaths: z
    .array(z.string().min(1).max(200))
    .min(1)
    .max(8)
    .describe('Envelope paths: body.id, headers.idempotency-key, query.nonce.'),
  windowSec: z.coerce.number().int().positive().max(604_800).default(300),
});

const transformSchema = z.object({
  ...ruleBase,
  type: z.literal(ERuleType.TRANSFORM),
  transformId: z.string().min(1).max(32),
  transformVersionId: z.string().min(1).max(32),
  version: z.coerce.number().int().nonnegative(),
  env: z.record(z.string(), z.string()).optional(),
  timeoutMs: z.coerce.number().int().positive().max(30_000).optional(),
  memoryMb: z.coerce.number().int().positive().max(512).optional(),
});

const filterSchema = z.object({
  ...ruleBase,
  type: z.literal(ERuleType.FILTER),
  condition: z.record(z.string(), z.unknown()).superRefine((value, ctx) => {
    try {
      parseFilterAst(value);
    } catch (error) {
      ctx.addIssue({
        code: 'custom',
        message: error instanceof Error ? error.message : 'invalid filter expression',
      });
    }
  }),
  scope: z.enum(EFilterScope).default(EFilterScope.BODY),
});

const delaySchema = z.object({
  ...ruleBase,
  type: z.literal(ERuleType.DELAY),
  delayMs: z.coerce.number().int().nonnegative().max(86_400_000).default(0),
});

const retrySchema = z.object({
  ...ruleBase,
  type: z.literal(ERuleType.RETRY),
});

export const RuleSchema = z.discriminatedUnion('type', [
  deduplicateSchema,
  transformSchema,
  filterSchema,
  delaySchema,
  retrySchema,
]);

// A check, not a sort: an illegal rule order is refused, never silently reordered.
export const ConnectionRulesSchema = z
  .array(RuleSchema)
  .max(DEFAULT_RULE_ORDER.length)
  .superRefine((rules, ctx) => {
    const types = rules.map((rule) => rule.type);

    if (new Set(types).size !== types.length) {
      ctx.addIssue({ code: 'custom', message: 'each rule type may appear at most once' });
    }

    const dedup = types.indexOf(ERuleType.DEDUPLICATE);
    if (dedup > 0) {
      ctx.addIssue({
        code: 'custom',
        message:
          'deduplicate must be the first rule: put anything before it and you pay to process ' +
          'events that are then thrown away',
      });
    }

    const retry = types.indexOf(ERuleType.RETRY);
    if (retry !== -1 && retry !== types.length - 1) {
      ctx.addIssue({
        code: 'custom',
        message:
          'retry must be the last rule: it decides what happens AFTER a delivery fails, so it ' +
          'is not a step in the pipeline',
      });
    }

    const delay = types.indexOf(ERuleType.DELAY);
    const filter = types.indexOf(ERuleType.FILTER);
    const transform = types.indexOf(ERuleType.TRANSFORM);
    if (
      delay !== -1 &&
      ((filter !== -1 && delay < filter) || (transform !== -1 && delay < transform))
    ) {
      ctx.addIssue({
        code: 'custom',
        message:
          'delay must come after transform and filter: there is no point waiting to send ' +
          'something that a later rule drops or rewrites',
      });
    }
  });

export const RetryPolicySchema = z.object({
  strategy: z.enum(['exponential', 'linear', 'fixed']).default('exponential'),
  initialSec: z.coerce.number().int().positive().max(86_400).default(20),
  multiplier: z.coerce.number().positive().max(100).default(2),
  capSec: z.coerce.number().int().positive().max(86_400).default(7_200),
  maxAttempts: z.coerce.number().int().positive().max(200).default(50),
  jitterPct: z.coerce.number().int().min(0).max(100).default(10),
  retryOn: z
    .object({
      status5xx: z.boolean().default(true),
      status429: z.boolean().default(true),
      honorRetryAfter: z.boolean().default(true),
      network: z.boolean().default(true),
      status4xx: z.boolean().default(false),
    })
    .prefault({}),
  onAmbiguous: z.enum(['retry', 'fail']).default('retry'),
});

export type TValidatedRules = z.infer<typeof ConnectionRulesSchema>;

export function safeParseRules(
  raw: unknown,
): { ok: true; rules: TPipelineRule[] } | { ok: false; error: string } {
  const parsed = ConnectionRulesSchema.safeParse(raw ?? []);
  if (!parsed.success) {
    return { ok: false, error: z.prettifyError(parsed.error) };
  }
  return { ok: true, rules: parsed.data as TPipelineRule[] };
}

export function safeParseRetryPolicy(raw: unknown): IPipelineRetryPolicy {
  const parsed = RetryPolicySchema.safeParse(raw ?? {});
  return (parsed.success ? parsed.data : RetryPolicySchema.parse({})) as IPipelineRetryPolicy;
}
